import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';

const { FiArrowLeft, FiCamera, FiSun, FiWind, FiMaximize2 } = FiIcons;

const photoSpots = [
  { title: "The R. Venue Balcony", description: "Our private 3rd-floor balconies offer the most intimate and unobstructed view of Mount Banahaw's peaks.", image: "https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvanBlZw==/99234c53776a5294a08f120f6976429f.jpeg" },
  { title: "Bukid Amara Flowers", description: "Located just minutes away, these vibrant flower fields provide a colorful foreground for the mountain backdrop.", image: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&q=80" }
];

const ScenicViews = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Scenic Views | R. Venue Apartelle Lucban",
    "description": "Capture breathtaking views of Mount Banahaw from R. Venue Apartelle. Explore Lucban's best viewpoints, from private balconies to mystical mountain mists.",
    "url": "https://rvenueapartelle.lucban.ph/scenic-views"
  };

  return (
    <div className="w-full">
      <SEO 
        title="Scenic Views"
        description="Capture breathtaking views of Mount Banahaw from R. Venue Apartelle. Explore Lucban's best viewpoints, from private balconies to mystical mountain mists."
        canonical="/scenic-views"
        ogImage="https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvanBlZw==/76cfaae74b7450930807c113255e5d5b.jpg"
        schema={pageSchema}
      />
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden pt-[100px]">
        <div className="absolute inset-0 z-0">
          <img src="https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvanBlZw==/76cfaae74b7450930807c113255e5d5b.jpg" alt="Majestic Mountain Views" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-brand-olive/70" />
        </div>
        <div className="relative z-10 text-center px-6 mt-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-16 h-16 bg-brand-cream/10 rounded-full flex items-center justify-center mx-auto mb-6 backdrop-blur-sm border border-white/20">
            <SafeIcon icon={FiCamera} className="text-brand-cream text-2xl" />
          </motion.div>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-script text-3xl text-brand-cream mb-4">Nature's Canvas</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="font-serif text-4xl md:text-6xl uppercase tracking-[0.2em] text-white">Scenic Views</motion.h1>
        </div>
      </section>

      <section className="py-24 bg-brand-cream">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <h2 className="font-serif text-3xl uppercase tracking-widest text-brand-olive mb-8">The Majesty of Mount Banahaw</h2>
          <div className="w-20 h-[1px] bg-brand-olive/30 mx-auto mb-10" />
          <div className="space-y-6 text-brand-olive/80 text-lg leading-relaxed font-light text-left md:text-center">
            <p>Lucban rests peacefully at the foothills of the mystical Mount Banahaw. The town's elevated geography gives it a refreshing breeze year-round.</p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white border-y border-brand-olive/5">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-4xl uppercase tracking-widest text-brand-olive mb-4">Lucban's Best Viewpoints</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {photoSpots.map((spot, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="group relative overflow-hidden bg-brand-cream/30 rounded-2xl flex flex-col md:flex-row h-full border border-brand-olive/5">
                <div className="w-full md:w-1/2 h-64 md:h-auto overflow-hidden"><img src={spot.image} alt={spot.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" /></div>
                <div className="w-full md:w-1/2 p-8 md:p-10 flex flex-col justify-center bg-white">
                  <h3 className="font-serif text-xl uppercase tracking-widest text-brand-olive mb-4">{spot.title}</h3>
                  <p className="text-brand-olive/70 text-sm leading-relaxed font-light">{spot.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-brand-cream text-center">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6">
            <Link to="/rates" className="btn-primary">Reserve Your View</Link>
            <Link to="/" className="inline-flex items-center gap-2 text-brand-olive font-bold uppercase tracking-widest text-xs hover:text-brand-dark transition-colors"><SafeIcon icon={FiArrowLeft} /> Back to Home</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ScenicViews;